import { AlertCircle } from 'lucide-react';
import { match } from 'ts-pattern';

import { cn } from '@nesso/ui/lib/utils';
import { Button } from '@nesso/ui/primitives/button';
import { CardFooter } from '@nesso/ui/primitives/card';

import type { SubscriptionMe } from '@/entities/subscription';

import { formatBillingFooterLine } from '../../../model/format-billing-footer-line';
import type { BillingStatus } from '../../../model/get-billing-status';

type FooterStyle = { shell: string; label: string; hasIcon: boolean };

// Both inactive states raise the same alarm, so they share one treatment.
const DESTRUCTIVE: FooterStyle = {
    shell: 'gap-3 bg-destructive-foreground',
    label: 'font-medium text-destructive',
    hasIcon: true,
};

const STYLE: Record<BillingStatus, FooterStyle> = {
    // `active` differs on purpose: the renewal design uses a tighter gap, and it is the only
    // state the design separates from the card content with a hairline.
    active: {
        shell: 'gap-2 border-t bg-background-muted',
        label: 'text-muted-foreground',
        hasIcon: false,
    },
    cancelling: {
        // Figma uses muted-foreground here, not Warning/text as Badge does.
        shell: 'gap-3 bg-warning',
        label: 'text-muted-foreground',
        hasIcon: false,
    },
    canceled: DESTRUCTIVE,
    'past-due': DESTRUCTIVE,
};

type Props = {
    status: BillingStatus;
    subscription: SubscriptionMe;
    scheduledPlanSessions: number | 'unlimited' | null;
    isReactivating: boolean;
    onManagePlan: () => void;
    onReactivate: () => void;
    onChoosePlan: () => void;
    onUpdateCard: () => void;
};

export function PlanBillingSummaryFooter({
    status,
    subscription,
    scheduledPlanSessions,
    isReactivating,
    onManagePlan,
    onReactivate,
    onChoosePlan,
    onUpdateCard,
}: Props) {
    const style = STYLE[status];
    const action = match(status)
        .with('active', () => ({ label: 'Manage Plan', onClick: onManagePlan, isDisabled: false }))
        // Reactivation stays in flight until the provider reports the plan running again, and a
        // second click would send a second request.
        .with('cancelling', () => ({
            label: isReactivating ? 'Reactivating' : 'Reactivate plan',
            onClick: onReactivate,
            isDisabled: isReactivating,
        }))
        .with('canceled', () => ({
            label: 'Choose Plan',
            onClick: onChoosePlan,
            isDisabled: false,
        }))
        .with('past-due', () => ({
            label: 'Update Card',
            onClick: onUpdateCard,
            isDisabled: false,
        }))
        .exhaustive();

    return (
        <CardFooter className={cn('justify-between py-4', style.shell)}>
            <div className="flex items-center gap-1.5">
                {style.hasIcon && (
                    // Lucide strokes the circle as well as filling it, and the stroke eats into its own fill.
                    <AlertCircle className="size-9 shrink-0 fill-destructive text-background [&>circle]:stroke-none" />
                )}
                <p className={cn('text-sm', style.label)}>
                    {formatBillingFooterLine(status, subscription, scheduledPlanSessions)}
                </p>
            </div>
            <Button
                variant="outline"
                size="sm"
                onClick={action.onClick}
                disabled={action.isDisabled}
            >
                {action.label}
            </Button>
        </CardFooter>
    );
}
